import React, { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { fadeUp, fadeIn, stagger, viewport } from "@/lib/motion";
import { Instagram, Linkedin, Mail, Calendar, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { SectionOrnament } from "@/components/SectionOrnament";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PORTRAIT =
  "https://images.unsplash.com/photo-1469474968028-56623f02e42e?auto=format&fit=crop&w=1400&q=80";

const INTENTS = [
  { value: "collaboration", label: "Collaboration" },
  { value: "journey", label: "A 1:1 Journey" },
  { value: "workshop", label: "Workshop / Facilitation" },
  { value: "speaking", label: "Speaking" },
  { value: "share", label: "Just to share something" },
];

export const LetsConnect = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    intent: "collaboration",
    message: "",
    preferred_time: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and a few words.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/inquiries`, form);
      setDone(true);
      toast.success("Received. I'll respond personally — with care.");
      setForm({ name: "", email: "", intent: "collaboration", message: "", preferred_time: "" });
    } catch (err) {
      console.error(err);
      toast.error("Something didn't go through. Please try again or email directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="connect"
      data-testid="connect-section"
      className="relative bg-bone py-28 md:py-44 overflow-hidden"
    >
      <SectionOrnament side="right" vertical="top-32" size={520} opacity={0.07} />
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 relative">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={viewport}
          variants={stagger(0.15)}
          className="grid md:grid-cols-12 gap-12 md:gap-16 items-start"
        >
          {/* Left — portrait + text */}
          <motion.div variants={fadeUp} className="md:col-span-5">
            <p className="text-[10px] uppercase tracking-[0.3em] text-bronze mb-6">
              ✦ &nbsp; 08 , Let&apos;s connect
            </p>
            <h2
              data-testid="connect-title"
              className="font-serif text-5xl md:text-7xl text-charcoal leading-[0.95] tracking-tight"
            >
              Collaboration
              <br />
              begins with
              <br />
              <span className="italic text-bronze">conversation.</span>
            </h2>

            <div className="zoom-container mt-12 aspect-[4/5] rounded-sm overflow-hidden">
              <img src={PORTRAIT} alt="A quiet space to talk" className="w-full h-full object-cover" />
            </div>
            <p className="mt-4 text-[10px] uppercase tracking-[0.3em] text-stone italic font-serif">
              The door is open. Take your time.
            </p>
          </motion.div>

          {/* Right — form + meta */}
          <motion.div variants={fadeUp} className="md:col-span-7">
            <p className="text-lg md:text-xl text-ink leading-relaxed font-light max-w-xl text-pretty">
              If something here resonates with you, I&apos;d love to hear from
              you. Whether it&apos;s a question, an idea, an invitation or a
              shared vision —{" "}
              <span className="font-serif italic text-bronze text-[1.4em]">
                let&apos;s explore it together.
              </span>
            </p>

            <form
              onSubmit={onSubmit}
              data-testid="connect-form"
              className="mt-12 space-y-7 max-w-xl"
            >
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <label className="text-[10px] uppercase tracking-[0.3em] text-stone block mb-2">
                    Your name
                  </label>
                  <input
                    data-testid="connect-name"
                    value={form.name}
                    onChange={onChange("name")}
                    placeholder="Who is writing"
                    className="w-full bg-transparent border-b border-stone/50 py-3 text-charcoal placeholder:text-stone/60 focus:outline-none focus:border-forest transition-colors"
                  />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.3em] text-stone block mb-2">
                    Email
                  </label>
                  <input
                    data-testid="connect-email"
                    type="email"
                    value={form.email}
                    onChange={onChange("email")}
                    placeholder="you@somewhere.com"
                    className="w-full bg-transparent border-b border-stone/50 py-3 text-charcoal placeholder:text-stone/60 focus:outline-none focus:border-forest transition-colors"
                  />
                </div>
              </div>

              <div>
                <label className="text-[10px] uppercase tracking-[0.3em] text-stone block mb-3">
                  This conversation is about…
                </label>
                <div className="flex flex-wrap gap-2">
                  {INTENTS.map((i) => (
                    <button
                      type="button"
                      key={i.value}
                      data-testid={`connect-intent-${i.value}`}
                      onClick={() => setForm((f) => ({ ...f, intent: i.value }))}
                      className={`px-4 py-2 rounded-full border text-[11px] uppercase tracking-[0.18em] transition-all duration-500 ${
                        form.intent === i.value
                          ? "bg-charcoal text-bone border-charcoal"
                          : "border-stone/50 text-ink hover:border-charcoal"
                      }`}
                    >
                      {i.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-[10px] uppercase tracking-[0.3em] text-stone block mb-2">
                  Your message
                </label>
                <textarea
                  data-testid="connect-message"
                  value={form.message}
                  onChange={onChange("message")}
                  rows={5}
                  placeholder="What would you like to share?"
                  className="w-full bg-transparent border-b border-stone/50 py-3 text-charcoal placeholder:text-stone/60 focus:outline-none focus:border-forest transition-colors resize-none"
                />
              </div>

              <div>
                <label className="text-[10px] uppercase tracking-[0.3em] text-stone block mb-2">
                  Preferred time / time zone <span className="lowercase text-stone/60">(optional)</span>
                </label>
                <input
                  data-testid="connect-time"
                  value={form.preferred_time}
                  onChange={onChange("preferred_time")}
                  placeholder="e.g. weekday mornings · EST"
                  className="w-full bg-transparent border-b border-stone/50 py-3 text-charcoal placeholder:text-stone/60 focus:outline-none focus:border-forest transition-colors"
                />
              </div>

              <div className="flex flex-wrap items-center gap-4 pt-2">
                <button
                  type="submit"
                  data-testid="connect-submit"
                  disabled={submitting || done}
                  className="btn-pill group inline-flex items-center gap-3 px-7 py-3.5 rounded-full bg-charcoal text-bone text-[11px] uppercase tracking-[0.22em] hover:bg-forest disabled:opacity-60"
                >
                  {done ? "Received — thank you" : submitting ? "Sending…" : "Send message"}
                  {!done && (
                    <ArrowRight size={14} className="transition-transform duration-500 group-hover:translate-x-1" />
                  )}
                </button>

                <a
                  href="https://calendly.com/harmoniaportal-5"
                  target="_blank"
                  rel="noreferrer"
                  data-testid="connect-calendly"
                  className="btn-pill inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-forest link-underline"
                >
                  <Calendar size={14} /> Or schedule a call
                </a>
              </div>
            </form>

            {/* Socials */}
            <div className="mt-16 pt-10 border-t border-stone/30">
              <p className="text-[10px] uppercase tracking-[0.3em] text-stone mb-5">Elsewhere</p>
              <div className="flex flex-wrap gap-6">
                <a
                  data-testid="connect-instagram"
                  href="https://www.instagram.com/harmoniaportal/"
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 text-charcoal link-underline text-sm"
                >
                  <Instagram size={16} /> Instagram
                </a>
                <a
                  data-testid="connect-linkedin"
                  href="https://www.linkedin.com/in/hadil-el-baba-a231a912a/"
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 text-charcoal link-underline text-sm"
                >
                  <Linkedin size={16} /> LinkedIn
                </a>
                <a
                  data-testid="connect-email-link"
                  href="mailto:harmoniaportal.5@gmail.com"
                  className="inline-flex items-center gap-2 text-charcoal link-underline text-sm"
                >
                  <Mail size={16} /> harmoniaportal.5@gmail.com
                </a>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default LetsConnect;
